/**
 * Elevación clínica: ticket ?t= autoriza el estudio; el plan del médico define herramientas.
 * Con ?edge_id= en el link: plan del vínculo links[] de esa clínica, si existe.
 */
import { ENTITLEMENT_SOURCES, UDV_PLANS } from './constants'
import { parsePlanQueryParam } from './parsePlanQuery'
import { resolveOriginLinkEntitlements } from './entitlements'

/**
 * @param {string|null|undefined} plan
 * @returns {boolean}
 */
export function isClinicalPlan(plan) {
  const p = parsePlanQueryParam(plan)
  return !!p && p !== UDV_PLANS.PATIENT
}

/**
 * Entitlements de sesión clínica (no ticket portal).
 * @param {object|null|undefined} ent
 * @returns {object|null}
 */
export function getClinicalEntitlements(ent) {
  if (!ent || !ent.plan) return null
  if (ent.source === ENTITLEMENT_SOURCES.TICKET) return null
  return isClinicalPlan(ent.plan) ? ent : null
}

/**
 * Entitlements que elevan un ticket ?t= (sesión Controller / origen / invite).
 * Ignora source URL: params staff en el link no deben elevar portal paciente.
 * @param {object|null|undefined} ent
 * @returns {object|null}
 */
export function getTicketClinicalEntitlements(ent) {
  if (!ent || !ent.plan) return null
  if (ent.source === ENTITLEMENT_SOURCES.TICKET) return null
  if (ent.source === ENTITLEMENT_SOURCES.URL) return null
  if (ent.source === ENTITLEMENT_SOURCES.PLATFORM_ELEVATION) return null
  if (ent.source === ENTITLEMENT_SOURCES.WORKSPACE_EMBED) return null
  return isClinicalPlan(ent.plan) ? ent : null
}

/**
 * ¿El médico tiene vínculo Controller para el edge_id del link?
 * @param {string|null|undefined} originEdgeId
 * @param {object[]|null|undefined} [controllerLinks]
 * @returns {object|null}
 */
export function getOriginLinkedEntitlements(originEdgeId, controllerLinks) {
  return resolveOriginLinkEntitlements(originEdgeId, controllerLinks)
}

/**
 * @param {object|null|undefined} ent
 * @param {{ token?: string }|null|undefined} [controllerSession]
 * @returns {{ elevated: boolean, plan: string|null, entitlements: object|null }}
 */
export function resolveTicketClinicalElevation(ent, controllerSession) {
  const clinical = getTicketClinicalEntitlements(ent)
  if (clinical) {
    return {
      elevated: true,
      plan: clinical.plan,
      entitlements: clinical,
    }
  }
  return { elevated: false, plan: null, entitlements: null }
}

/**
 * Elevación para ticket con origen explícito (?edge_id=).
 * @param {string|null|undefined} originEdgeId
 * @param {object[]|null|undefined} controllerLinks
 * @param {object|null|undefined} cachedEnt
 * @param {{ token?: string }|null|undefined} [controllerSession]
 * @returns {{
 *   mode: 'clinical'|'portal'|'legacy',
 *   elevated: boolean,
 *   plan: string|null,
 *   entitlements: object|null,
 * }}
 */
export function resolveOriginTicketElevation(
  originEdgeId,
  controllerLinks,
  cachedEnt,
  controllerSession
) {
  const want =
    originEdgeId != null && String(originEdgeId).trim() !== ''
      ? String(originEdgeId).trim()
      : null
  if (!want) {
    const leg = resolveTicketClinicalElevation(cachedEnt, controllerSession)
    return {
      mode: 'legacy',
      elevated: leg.elevated,
      plan: leg.plan,
      entitlements: leg.entitlements,
    }
  }

  const originEnt = getOriginLinkedEntitlements(want, controllerLinks)
  if (originEnt) {
    return {
      mode: 'clinical',
      elevated: true,
      plan: originEnt.plan,
      entitlements: originEnt,
    }
  }

  return {
    mode: 'portal',
    elevated: false,
    plan: UDV_PLANS.PATIENT,
    entitlements: null,
  }
}

/**
 * Perfil efectivo para pickViewerCandidate (LAN vs WAN).
 * @param {string} ticketProfile
 * @param {boolean} elevated
 * @param {string|null} [clinicalPlan]
 */
export function pickProfileForTicketLoad(ticketProfile, elevated, clinicalPlan) {
  if (elevated && clinicalPlan) return clinicalPlan
  if (elevated) return UDV_PLANS.LITE
  return ticketProfile || UDV_PLANS.PATIENT
}
